import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiArrowRight,
  FiCheckCircle,
  FiCompass,
  FiMapPin,
  FiStar,
} from "react-icons/fi";
import SafariCard from "../components/SafariCard";
import { safariTrips } from "../data/safariTrips";
import gorilla from "../assets/gorilla.jpg";
import lions from "../assets/lions.jpg";
import beach from "../assets/beach.jpg";
import cranes from "../assets/CrestedCranes.jpg";
import leopard from "../assets/Leopaed.jpg";
import heino from "../assets/heino.jpg";

const destinations = [
  {
    name: "Uganda",
    image: gorilla,
    tagline: "Gorillas, chimpanzees, river safaris, and rich cultural routes.",
    highlights: ["Bwindi", "Kibale Forest", "Murchison Falls"],
  },
  {
    name: "Kenya",
    image: leopard,
    tagline: "Classic big game safaris, conservancies, and dramatic savannahs.",
    highlights: ["Maasai Mara", "Amboseli", "Lake Nakuru"],
  },
  {
    name: "Tanzania",
    image: lions,
    tagline: "Serengeti plains, Ngorongoro Crater, Kilimanjaro, and Zanzibar.",
    highlights: ["Serengeti", "Ngorongoro", "Zanzibar"],
  },
  {
    name: "Rwanda",
    image: cranes,
    tagline: "Premium gorilla trekking, clean cities, forests, and lakeside escapes.",
    highlights: ["Volcanoes", "Akagera", "Lake Kivu"],
  },
  {
    name: "Zanzibar",
    image: beach,
    tagline: "White sand beaches, spice culture, Stone Town, and ocean relaxation.",
    highlights: ["Nungwi", "Stone Town", "Mnemba"],
  },
  {
    name: "Namibia",
    image: heino,
    tagline: "Desert dunes, Atlantic coast, cultural encounters, and private 4x4 routes.",
    highlights: ["Sossusvlei", "Swakopmund", "Walvis Bay"],
  },
];

const planningPoints = [
  "Cross-border safari routes arranged around realistic travel times",
  "Gorilla, chimpanzee, wildlife, culture, and beach extensions",
  "Luxury, midrange, family, honeymoon, and private group options",
];

const Destination = ({ name = "East Africa Destinations", image }) => {
  const bannerImage = image || lions;
  const featuredTrips = safariTrips.slice(0, 3);

  return (
    <div className="bg-[#f6f1e7] font-[Poppins]">
      <section className="relative min-h-[70vh] overflow-hidden pt-28 text-white">
        <img
          src={bannerImage}
          alt={name}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#03140d]/95 via-[#03140d]/70 to-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#f6f1e7] to-transparent" />

        <div className="relative z-10 mx-auto flex min-h-[70vh] max-w-7xl items-center px-4 pb-20 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <div className="mb-5 inline-flex items-center gap-2 border-l-4 border-[#F5B731] bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#F5B731] backdrop-blur">
              <FiCompass />
              Destinations
            </div>
            <h1 className="text-4xl font-extrabold uppercase leading-tight tracking-normal sm:text-5xl lg:text-6xl">
              Explore Africa's Most Remarkable Safari Places
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-8 text-white/80 sm:text-lg">
              Discover gorilla forests, savannah parks, crater landscapes,
              deserts, cultures, and Indian Ocean beaches with travel planning
              that connects every detail.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/tours"
                className="inline-flex items-center gap-2 rounded-sm bg-[#F5B731] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#082f1f] transition hover:bg-white"
              >
                View Safari Packages
                <FiArrowRight />
              </Link>
              <Link
                to="/contact"
                className="rounded-sm border border-white/30 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:border-[#F5B731] hover:text-[#F5B731]"
              >
                Plan A Route
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#C88804]">
              Where To Go
            </p>
            <h2 className="mt-3 max-w-3xl text-3xl font-extrabold uppercase leading-tight text-[#082f1f] sm:text-4xl">
              Signature Destinations For Wildlife And Leisure
            </h2>
          </div>
          <p className="max-w-xl text-sm leading-7 text-[#5d7168]">
            Combine countries or focus deeply on one region. Each destination is
            selected for wildlife quality, access, guiding, and memorable stays.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {destinations.map((destination, index) => (
            <motion.article
              key={destination.name}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: index * 0.06 }}
              whileHover={{ y: -8 }}
              className="group overflow-hidden rounded-sm bg-white shadow-xl shadow-black/10"
            >
              <div className="relative h-72 overflow-hidden">
                <img
                  src={destination.image}
                  alt={destination.name}
                  className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-[#F5B731]">
                    <FiMapPin />
                    East Africa
                  </div>
                  <h3 className="text-2xl font-extrabold">{destination.name}</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm leading-7 text-[#5d7168]">
                  {destination.tagline}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {destination.highlights.map((highlight) => (
                    <span
                      key={highlight}
                      className="rounded-sm bg-[#f6f1e7] px-3 py-1 text-xs font-bold text-[#315245]"
                    >
                      {highlight}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
          <div className="rounded-sm bg-[#082f1f] p-7 text-white shadow-2xl shadow-black/15 sm:p-9">
            <div className="mb-5 inline-flex items-center gap-2 border-l-4 border-[#F5B731] bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#F5B731]">
              <FiStar />
              Route Planning
            </div>
            <h2 className="text-3xl font-extrabold uppercase leading-tight sm:text-4xl">
              Build A Safari Route That Makes Sense
            </h2>
            <p className="mt-5 text-sm leading-7 text-white/75">
              The best trip is not only about where you go. It is about timing,
              distance, permits, lodge choice, pace, and how each experience
              connects to the next.
            </p>
          </div>

          <div className="grid gap-4">
            {planningPoints.map((point) => (
              <div key={point} className="flex gap-4 rounded-sm bg-[#fbf8f0] p-5">
                <FiCheckCircle className="mt-1 shrink-0 text-xl text-[#C88804]" />
                <p className="text-sm leading-7 text-[#5d7168]">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="mb-8">
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#C88804]">
            Featured Packages
          </p>
          <h2 className="mt-3 text-3xl font-extrabold uppercase text-[#082f1f] sm:text-4xl">
            Safaris Across These Destinations
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {featuredTrips.map((trip, index) => (
            <SafariCard key={trip.id} safari={trip} index={index} />
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/tours"
            className="inline-flex items-center gap-2 rounded-sm bg-[#082f1f] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#F5B731] hover:text-[#082f1f]"
          >
            Show All Safaris
            <FiArrowRight />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Destination;
